import React from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import { Chart } from "react-chartjs-2";

ChartJS.register(...registerables, MatrixController, MatrixElement);

function PeakActivityGraph({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    // return <div style={{ textAlign: "center" }}>No peak activity data available</div>;
    return <div></div>;
  }

  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Initialize activity matrix
  const activity = {};
  weekDays.forEach((day) => (activity[day] = Array(24).fill(0)));

  // Fill matrix with activity data
  data.forEach((company) => {
    company.data.forEach((entry) => {
      const d = new Date(entry.created_at);
      const day = weekDays[(d.getDay() + 6) % 7]; // Monday = 0
      const hour = d.getHours();
      activity[day][hour]++;
    });
  });

  // Flatten data for chart.js
  const matrixData = [];
  weekDays.forEach((day) => {
    hours.forEach((hour) => {
      matrixData.push({ x: day, y: hour, v: activity[day][hour] });
    });
  });

  // Compute color scale
  const allValues = matrixData.map((d) => d.v);
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);

  const getColor = (v) => {
    if (v === 0) return "rgba(164, 201, 243, 0.4)"; // pale blue for no activity
    const intensity = (v - minValue) / (maxValue - minValue || 1);
    const alpha = 0.3 + intensity * 0.7;
    return `rgba(0, 123, 255, ${alpha})`;
  };

  const chartData = {
    datasets: [
      {
        label: "Peak Activity",
        data: matrixData,
        backgroundColor: (ctx) => getColor(ctx.raw.v),
        borderColor: (ctx) => {
          const dayIndex = weekDays.indexOf(ctx.raw.x);
          // Thicker border for vertical weekday separators
          return dayIndex < weekDays.length - 1
            ? "rgba(255,255,255,0.9)"
            : "rgba(255,255,255,0.9)";
        },
        borderWidth: (ctx) => {
          const dayIndex = weekDays.indexOf(ctx.raw.x);
          // Slightly thicker borders at day boundaries
          return { top: 1, bottom: 1, left: dayIndex === 0 ? 2 : 0.5, right: 1.5 };
        },
        width: (ctx) => {
          const chart = ctx.chart;
          if (!chart.chartArea) return 20;
          return chart.chartArea.width / weekDays.length;
        },
        height: (ctx) => {
          const chart = ctx.chart;
          if (!chart.chartArea) return 20;
          return chart.chartArea.height / 24;
        },
      },
    ],
  };

  // Chart configuration
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    alignToPixels: true,
    elements: { rectangle: { borderSkipped: false } },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Peak Activity",
        font: { size: 18 },
        color: "#333",
      },
      tooltip: {
        callbacks: {
          title: (ctx) => {
            const { x, y } = ctx[0].raw;
            return `${x} at ${y}:00`;
          },
          label: (ctx) => `${ctx.raw.v} activities`,
        },
      },
    },
    scales: {
      x: {
        type: "category",
        labels: weekDays,
        offset: true,
        title: {
          display: true,
          text: "Weekday",
          font: { weight: "bold" },
        },
        grid: {
          display: false,
          drawBorder: true,
        },
      },
      y: {
        type: "linear",
        min: 0,
        max: 23,
        offset: true,
        reverse: true,
        ticks: {
          stepSize: 2,
          callback: (val) => `${val}:00`,
        },
        title: {
          display: true,
          text: "Hour of Day",
          font: { weight: "bold" },
        },
        grid: {
          display: false,
          drawBorder: true,
        },
      },
    },
  };

  return (
    <div style={{ width: "90%", height: "500px", margin: "2rem auto" }}>
      <Chart type="matrix" data={chartData} options={options} />
    </div>
  );
}

export default PeakActivityGraph;
